/**
 * Soft≠Live — Settings People & access (Fact 3 create seat).
 * Mount: /settings/users
 * Buses: get__auth_users · post__auth_users
 * Ports the leftover SPA create slice — does not remount #/admin.
 */
import { useMemo, useState } from "react";
import { html, Ico, Card, Field, Input, Alert, KV } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";

/** Path-free operator copy. */
export function softUsersSettingsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied|insufficient/.test(low)) {
    return "Your account cannot add people here. Ask a manager if you need access.";
  }
  if (/\b401\b|unauthorized|missing bearer|session/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b422\b|\b400\b|invalid|required|email|phone|password|username/.test(low)) {
    return "That seat was not accepted. Check username, email, phone, and password.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|account store/.test(low)) {
    return "Account store is busy — try again in a moment.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/auth\/|GET |POST |DELETE |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

function safeUsersPath(op) {
  try {
    return path(op);
  } catch (_e) {
    if (op === "get__auth_users") return "/auth/users";
    if (op === "post__auth_users") return "/auth/users";
    if (op === "get__settings_status") return "/settings/status";
    return "";
  }
}

export function usersRowsOf(pack, maxN) {
  const root = pack || {};
  const rows = root.items || root.users || root.rows || [];
  const list = Array.isArray(rows) ? rows : [];
  const n = Number(maxN);
  const cap = Number.isFinite(n) && n > 0 ? Math.floor(n) : 80;
  return list.slice(0, cap);
}

export function UsersSettingsDesk({ toast }) {
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const isSuper = role === "superadmin";
  const list = useApi(safeUsersPath("get__auth_users"));
  const shellPosture = useApi(safeUsersPath("get__settings_status"));
  const createOk = shellPosture.loading ? false : (
    !!(shellPosture.data && shellPosture.data.users_pane_allowed === true)
  );
  const { run, busy, error, envelope } = useAction();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [seatRole, setSeatRole] = useState("user");
  const [manager, setManager] = useState("");

  const rows = useMemo(() => usersRowsOf(list.data, 80), [list.data]);
  const admins = useMemo(
    () => rows.filter((r) => r && (r.role === "admin" || r.role === "superadmin")),
    [rows],
  );

  const reload = () => {
    list.reload && list.reload();
    toast && toast("People list refreshed", "go");
  };

  const createSeat = async () => {
    if (!createOk) {
      toast && toast("Your account cannot add people here. Ask a manager if you need access.", "warn");
      return;
    }
    const body = {
      username: username.trim(),
      password,
      role: seatRole === "admin" ? "admin" : "user",
      email: email.trim(),
      phone: phone.trim(),
    };
    if (!body.username || !body.password) {
      toast && toast("Username and password are required.", "warn");
      return;
    }
    if (body.password.length < 12) {
      toast && toast("Password must be at least 12 characters.", "warn");
      return;
    }
    if (!body.email.includes("@")) {
      toast && toast("Email is required for activation.", "warn");
      return;
    }
    if (!body.phone) {
      toast && toast("Phone is required.", "warn");
      return;
    }
    if (isSuper && body.role === "user") {
      const mgr = manager.trim() || String((admins[0] && admins[0].username) || "");
      if (!mgr) {
        toast && toast("Select a manager admin for this end user.", "warn");
        return;
      }
      body.manager_admin = mgr;
    }
    const out = await run(safeUsersPath("post__auth_users"), { body });
    if (!out) {
      toast && toast(softUsersSettingsErrorOf(error) || "Could not create that seat.", "bad");
      return;
    }
    setUsername("");
    setEmail("");
    setPhone("");
    setPassword("");
    setSeatRole("user");
    list.reload && list.reload();
    toast && toast("Seat created. Ask them to install the phone and share GPS.", "go");
  };

  return html`<div className="users-desk users-desk--liquid" data-cmc-page="settings-users">
    <div className="notify-desk__rail">
      <div>
        <h2 className="notify-desk__title">People & access</h2>
        <p className="note">Ranked dispatch stays closed without a shared point — this desk never invents coordinates.</p>
      </div>
      <button type="button" className="btn btn--ghost btn--sm"
        disabled=${busy || list.loading} onClick=${reload}>
        <${Ico} d="refresh" w=${13}/>Refresh
      </button>
    </div>

    ${envelope
      ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
      : null}

    <${Card} title="Seats on this site" meta="directory">
      ${list.loading && !list.data
        ? html`<p className="note">Reading seats…</p>`
        : list.error
          ? html`<${Alert} tone="warn" title="People list unavailable"
              say=${softUsersSettingsErrorOf(list.error)}/>`
          : html`<div className="rows">
              ${!rows.length ? html`<p className="note">No seats listed yet.</p>` : null}
              ${rows.map((r) => html`
                <div key=${r.username} className="row">
                  <span className="st"/>
                  <span className="main">
                    <b>${r.username}</b>
                    <span>${r.role || "—"} · ${r.email || "no email"}
                      ${r.phone_verified_at ? " · phone verified" : " · phone pending"}</span>
                  </span>
                </div>`)}
            </div>`}
    <//>

    <${Card} title="New seat" meta="create" cls="mt"
        sub="Activation code is queued — not a delivered proof.">
      <div className="grid g2" style=${{ gap: "0 16px" }}>
        <${Field} label="Username">
          <${Input} icon="user" value=${username} onInput=${(e) => setUsername(e.target.value)}
            autocomplete="off" placeholder="night.desk"/>
        <//>
        <${Field} label="Email (required)">
          <${Input} icon="mail" type="email" value=${email} onInput=${(e) => setEmail(e.target.value)}
            autocomplete="off" placeholder="ops@example.invalid"/>
        <//>
        <${Field} label="Phone (required)">
          <${Input} icon="phone" value=${phone} onInput=${(e) => setPhone(e.target.value)}
            autocomplete="off" placeholder="+60123456789"/>
        <//>
        <${Field} label="Password (min 12)">
          <${Input} icon="lock" type="password" value=${password}
            onInput=${(e) => setPassword(e.target.value)} autocomplete="new-password"/>
        <//>
      </div>
      <p className="note">Uppercase, lowercase, digit, and a symbol.</p>
      <${Field} label="Role">
        <select className="input" value=${seatRole} onChange=${(e) => setSeatRole(e.target.value)}>
          <option value="user">End user</option>
          <option value="admin">${isSuper ? "Admin" : "Admin (staff)"}</option>
        </select>
      <//>
      ${isSuper && seatRole === "user" ? html`
        <${Field} label="Manager admin">
          <select className="input" value=${manager} onChange=${(e) => setManager(e.target.value)}>
            <option value="">Select a manager…</option>
            ${admins.map((a) => html`<option key=${a.username} value=${a.username}>${a.username}</option>`)}
          </select>
        <//>` : null}
      <div className="btn-row" style=${{ marginTop: 12 }}>
        <button type="button" className="btn btn--go btn--sm"
          data-cmc-users-create-ok=${createOk ? "1" : "0"}
          disabled=${busy || !createOk || !username.trim() || password.length < 12 || !email.includes("@") || !phone.trim()}
          onClick=${createSeat}>
          <${Ico} d="plus" w=${13}/> Create & queue activation
        </button>
      </div>
    <//>
  </div>`;
}

export default UsersSettingsDesk;
